import {
  useAppDispatch,
  useAppSelector,
} from "../../../../stores/hooks/hooks.ts";

import { RevButton } from "@revmatics/catalyst";
import { PersonaDisclosureFilter } from "./components/personaDisclosureFilter/PersonaDisclosureFilter.tsx";
import { InterestDisclosureFilter } from "./components/interestDisclosureFilter/InterestDisclosureFilter.tsx";
import { CommunityDisclosureFilter } from "./components/communityDisclosureFilter/CommunityDisclosureFilter.tsx";
import { FilterParamsType } from "../../discoverCommunitiesTypes.ts";

import { useCallback, useEffect } from "react";
import { PersonaOverview } from "./components/personaOverview/PersonaOverview.tsx";
import { CommunityInsights } from "./components/communityInsights/CommunityInsights.tsx";
import { TopCreators } from "./components/topCreators/TopCreators.tsx";
import {
  fetchPersonaCommunitiesAggregateData,
  fetchPersonaCommunitiesData,
} from "../../../../stores/thunks/discoverCommunitiesThunk.tsx";
import { CommunityDataItemType } from "../../../../stores/types/discoverCommunitiesTypes.tsx";
import { InterestType } from "../../../../stores/types/personasTypes.tsx";

type Props = {
  selectedInterests: Set<string>;
  setSelectedInterests: (value: Set<string>) => void;
  selectedCommunity: Set<number>;
  setSelectedCommunity: (value: Set<number>) => void;
  filter: FilterParamsType;
  setFilter: React.Dispatch<React.SetStateAction<FilterParamsType>>;
  selectedAffiliation: Set<string>;
  setSelectedAffiliation: (value: Set<string>) => void;
  setSelectedParentInterest: (value: InterestType | null) => void;
  selectedParentInterest: InterestType | null;
  selectedInterestsCommunity: Set<string>;
  setSelectedInterestsCommunity: (value: Set<string>) => void;
  isFirstLoading: boolean;
};

export const TraditionalView = ({
  setSelectedInterests,
  selectedInterests,
  setSelectedCommunity,
  selectedCommunity,
  filter,
  setFilter,
  selectedAffiliation,
  setSelectedAffiliation,
  setSelectedParentInterest,
  selectedParentInterest,
  selectedInterestsCommunity,
  setSelectedInterestsCommunity,
  isFirstLoading,
}: Props) => {
  const dispatch = useAppDispatch();
  const communities = useAppSelector(
    (state) => state.discoverCommunities.communities,
  );
  const isLoadingCommunityAggregate = useAppSelector(
    (state) => state.discoverCommunities.isLoadingCommunityAggregate,
  );
  const selectedPersona = useAppSelector(
    (state) => state.discoverCommunities.selectedPersona,
  );

  const clearFilters = useCallback(() => {
    setSelectedParentInterest(null);
    setSelectedInterests(new Set());
    setFilter({
      name: "",
      sort: "-size",
      min_size: "",
      max_size: "",
    });
    setSelectedAffiliation(new Set());
    setSelectedInterestsCommunity(new Set());
  }, [
    setFilter,
    setSelectedAffiliation,
    setSelectedInterests,
    setSelectedParentInterest,
    setSelectedInterestsCommunity,
  ]);

  useEffect(() => {
    if (communities && communities.length > 0) {
      setSelectedCommunity(
        new Set(
          communities.map((item: CommunityDataItemType) => item.community.id),
        ),
      );
    } else {
      setSelectedCommunity(new Set());
    }
  }, [communities, setSelectedCommunity]);

  const applyFilters = () => {
    if (selectedPersona) {
      dispatch(
        fetchPersonaCommunitiesAggregateData({
          id: selectedPersona.id.toString(),
          body: {
            community_ids: [...selectedCommunity],
          },
        }),
      );
    }
  };

  useEffect(() => {
    clearFilters();
  }, [selectedPersona, clearFilters]);

  const handleChangeFilter = useCallback(
    (key: string, value: string) => {
      setFilter((prev: FilterParamsType) => ({ ...prev, [key]: value }));
    },
    [setFilter],
  );

  return (
    <div className="py-6 flex flex-col gap-6">
      <div className="shadow-elevation-1 bg-primary-50 rounded-lg overflow-hidden">
        <div className="px-6 py-2">
          <h5>Filters</h5>
        </div>
        <PersonaDisclosureFilter />
        <InterestDisclosureFilter
          isFirstLoading={isFirstLoading}
          selectedCommunity={selectedCommunity}
          selectedInterests={selectedInterests}
          setSelectedCommunity={setSelectedCommunity}
          setSelectedInterests={setSelectedInterests}
          selectedParentInterest={selectedParentInterest}
          setSelectedParentInterest={setSelectedParentInterest}
          selectedInterestsCommunity={selectedInterestsCommunity}
          setSelectedInterestsCommunity={setSelectedInterestsCommunity}
        />
        <CommunityDisclosureFilter
          selectedCommunity={selectedCommunity}
          setSelectedCommunity={setSelectedCommunity}
          selectedInterests={selectedInterests}
          setSelectedInterests={setSelectedInterests}
          filter={filter}
          selectedAffiliation={selectedAffiliation}
          setSelectedAffiliation={setSelectedAffiliation}
          handleChangeFilter={handleChangeFilter}
          selectedInterestsCommunity={selectedInterestsCommunity}
          setSelectedInterestsCommunity={setSelectedInterestsCommunity}
        />
        <div className="py-2 px-6 border-t border-monochrome-200 flex gap-x-3 justify-end">
          <RevButton
            text="Clear Filters"
            buttonSize="small"
            variant="secondary"
            data-testid="discover-communities-clear-filter"
            handleClick={() => {
              clearFilters();
              if (selectedPersona) {
                dispatch(
                  fetchPersonaCommunitiesData({
                    id: selectedPersona.id.toString(),
                  }),
                );
              }
              applyFilters();
            }}
          />
          <RevButton
            text="Apply"
            data-testid="discover-communities-apply"
            buttonSize="small"
            variant="primary"
            handleClick={applyFilters}
            isLoading={isLoadingCommunityAggregate}
          />
        </div>
      </div>
      <PersonaOverview />
      <CommunityInsights />
      <TopCreators />
    </div>
  );
};
